import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { collection, addDoc, serverTimestamp, query, orderBy, getDocs, limit, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../firebase/AuthContext';
import { logOut, getTrialDaysLeft } from '../firebase/auth';
 
function Upload() {
  const navigate = useNavigate();
  const { user, userData, orgData } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [recentProjects, setRecentProjects] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    partNumber: '',
    revision: '',
    drawingNumber: '',
    material: '',
    finish: '',
    units: 'INCH'
  });
 
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });
 
  // Trial countdown
  const daysLeft = orgData?.plan === 'trial' ? getTrialDaysLeft(orgData?.trialEnds) : null;
 
  const getTrialColors = (days) => {
    if (days > 14) return { bg: '#eff6ff', text: '#2563EB', border: '#bfdbfe', dot: '#2563EB' };
    if (days > 7)  return { bg: '#f0fdf4', text: '#16a34a', border: '#86efac', dot: '#16a34a' };
    if (days > 3)  return { bg: '#fffbeb', text: '#d97706', border: '#fcd34d', dot: '#d97706' };
    return               { bg: '#fef2f2', text: '#dc2626', border: '#fecaca', dot: '#dc2626' };
  };
 
  const trialColors = daysLeft !== null ? getTrialColors(daysLeft) : null;
 
  const trialLabel = (days) => {
    if (days <= 1) return 'Trial expires today';
    if (days <= 3) return `${days} days left`;
    return `${days} days left in trial`;
  };
 
  // Load recent projects — filtered by orgId
  useEffect(() => {
    const loadRecent = async () => {
      if (!userData?.orgId) return;
      try {
        const q = query(
          collection(db, 'projects'),
          where('orgId', '==', userData.orgId),
          orderBy('createdAt', 'desc'),
          limit(8)
        );
        const snap = await getDocs(q);
        const projects = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRecentProjects(projects);
      } catch (err) {
        console.error(err);
      }
    };
    loadRecent();
  }, [userData]);
 
  // Search — filtered by orgId
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }
    const timeout = setTimeout(async () => {
      if (!userData?.orgId) return;
      setSearching(true);
      try {
        const q = query(
          collection(db, 'projects'),
          where('orgId', '==', userData.orgId),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        const term = searchTerm.toLowerCase();
        const results = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p =>
            (p.partNumber || '').toLowerCase().includes(term) ||
            (p.customerName || '').toLowerCase().includes(term) ||
            (p.drawingNumber || '').toLowerCase().includes(term) ||
            (p.fileName || '').toLowerCase().includes(term)
          )
          .slice(0, 8);
        setSearchResults(results);
        setShowSearch(true);
      } catch (err) {
        console.error(err);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, userData]);
 
  const handleSubmit = async () => {
    if (!acceptedFiles[0]) return setError('Please upload a PDF first.');
    if (!formData.partNumber) return setError('Part number is required.');
    setUploading(true);
    setError('');
    try {
      const file = acceptedFiles[0];
      const storageRef = ref(storage, `pdfs/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const pdfURL = await getDownloadURL(storageRef);
      const docRef = await addDoc(collection(db, 'projects'), {
        ...formData,
        pdfURL,
        fileName: file.name,
        bubbles: [],
        createdAt: serverTimestamp(),
        status: 'review',
        orgId: userData?.orgId,
        createdBy: user?.uid
      });
      navigate(`/review/${docRef.id}`);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setUploading(false);
  };
 
  const handleNewRun = async (e, project) => {
    e.stopPropagation();
    const jobNumber = prompt(`New Job Number for ${project.partNumber}:`, `JOB-${new Date().toISOString().split('T')[0]}`);
    if (!jobNumber) return;
    try {
      const freshBubbles = (project.bubbles || []).map(b => ({ ...b, status: 'Pending' }));
      const docRef = await addDoc(collection(db, 'projects'), {
        customerName: project.customerName || '',
        partNumber: project.partNumber,
        revision: project.revision || '',
        drawingNumber: project.drawingNumber || '',
        material: project.material || '',
        finish: project.finish || '',
        units: project.units || 'INCH',
        pdfURL: project.pdfURL,
        fileName: project.fileName,
        bubbles: freshBubbles,
        actuals: {},
        jobNumber,
        inspectionDate: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp(),
        status: 'review',
        orgId: userData?.orgId,
        createdBy: user?.uid,
        duplicatedFrom: project.id
      });
      navigate(`/review/${docRef.id}`);
    } catch (err) {
      console.error(err);
      alert('Error creating new run. Please try again.');
    }
  };
 
  const handleSignOut = async () => {
    await logOut();
    navigate('/login');
  };
 
  const getStatusColor = (status) => {
    if (status === 'complete') return '#16a34a';
    if (status === 'fail') return '#dc2626';
    return '#2563EB';
  };
 
  const getStatusLabel = (status) => {
    if (status === 'complete') return 'Complete';
    if (status === 'fail') return 'Failed';
    return 'In Progress';
  };
 
  const getStatusBg = (status) => {
    if (status === 'complete') return '#dcfce7';
    if (status === 'fail') return '#fef2f2';
    return '#eff6ff';
  };
 
  const getAccentColor = (status) => {
    if (status === 'complete') return '#16a34a';
    if (status === 'fail') return '#dc2626';
    return '#2563EB';
  };
 
  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
 
  return (
    <div style={styles.page}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br/>Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={{...styles.navItem, ...styles.navActive}}>
            <span>⬆</span> Upload Print
          </div>
          <div style={styles.navItem}><span>◎</span> Review Bubbles</div>
          <div style={styles.navItem}><span>☰</span> FAI Document</div>
          <div style={styles.navItem}><span>✓</span> Final Inspection</div>
          <div style={styles.navItem}><span>↓</span> Exports</div>
          <div style={styles.navItem} onClick={() => navigate('/team')}><span>👥</span> Team</div>
        </nav>
        <div style={styles.sidebarBottom}>
          {userData?.companyName && (
            <div style={styles.companyChip}>{userData.companyName}</div>
          )}
          <div style={styles.userEmail}>{user?.email}</div>
          <button style={styles.signOutBtn} onClick={handleSignOut}>Sign Out</button>
        </div>
      </div>
 
      {/* MAIN */}
      <div style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitle}>Upload Print</h1>
            <p style={styles.headerSub}>Upload a manufacturing PDF or search for an existing project</p>
          </div>
 
          {/* TRIAL BADGE — right side of header, only shows during trial */}
          {daysLeft !== null && daysLeft > 0 && trialColors && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: trialColors.bg,
              border: `1px solid ${trialColors.border}`,
              borderRadius: '10px',
              padding: '8px 14px',
            }}>
              {/* Pulsing dot */}
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: trialColors.dot,
                flexShrink: 0,
              }} />
              <div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: trialColors.text,
                  lineHeight: 1.2,
                }}>
                  {trialLabel(daysLeft)}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: trialColors.text,
                  opacity: 0.75,
                  marginTop: '1px',
                }}>
                  Free trial · Full access
                </div>
              </div>
              <a
                href="mailto:JimmyKDiaz@gmail.com?subject=MIA Subscription - Ready to upgrade"
                style={{
                  marginLeft: '6px',
                  background: trialColors.text,
                  color: '#fff',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Choose a Plan
              </a>
            </div>
          )}
        </div>
 
        <div style={styles.workspace}>
 
          {/* LEFT — upload form */}
          <div style={styles.leftCol}>
 
            {/* SEARCH BAR */}
            <div style={styles.searchSection}>
              <div style={styles.searchWrap}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  style={styles.searchInput}
                  placeholder="Search by drawing number, part number, or customer name..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowSearch(true)}
                />
                {searchTerm && (
                  <button style={styles.clearBtn} onClick={() => { setSearchTerm(''); setShowSearch(false); setSearchResults([]); }}>✕</button>
                )}
              </div>
 
              {showSearch && (
                <div style={styles.searchResults}>
                  {searching ? (
                    <div style={styles.searchLoading}>Searching...</div>
                  ) : searchResults.length === 0 ? (
                    <div style={styles.searchEmpty}>No projects found for "{searchTerm}"</div>
                  ) : (
                    searchResults.map(p => (
                      <div key={p.id} style={styles.searchResult}>
                        <div style={styles.resultLeft} onClick={() => navigate(`/review/${p.id}`)}>
                          <div style={styles.resultIcon}>📄</div>
                          <div>
                            <div style={styles.resultName}>
                              {p.partNumber}
                              {p.revision && <span style={styles.resultRev}> Rev {p.revision}</span>}
                              {p.jobNumber && <span style={styles.resultJob}> · {p.jobNumber}</span>}
                            </div>
                            <div style={styles.resultMeta}>
                              {p.customerName && <span>{p.customerName} · </span>}
                              {p.fileName} · {(p.bubbles || []).length} bubbles
                            </div>
                          </div>
                        </div>
                        <div style={styles.resultRight}>
                          <span style={{ ...styles.resultStatus, background: getStatusBg(p.status), color: getStatusColor(p.status) }}>
                            {getStatusLabel(p.status)}
                          </span>
                          <button style={styles.openBtn} onClick={() => navigate(`/review/${p.id}`)}>Open</button>
                          <button style={styles.newRunBtn} onClick={(e) => handleNewRun(e, p)}>+ New Run</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
 
            <div style={styles.dividerRow}>
              <div style={styles.dividerLine}></div>
              <div style={styles.dividerText}>or upload a new print</div>
              <div style={styles.dividerLine}></div>
            </div>
 
            {/* DROP ZONE */}
            <div {...getRootProps()} style={{
              ...styles.dropzone,
              ...(isDragActive ? styles.dropzoneActive : {}),
              ...(acceptedFiles[0] ? styles.dropzoneSuccess : {})
            }}>
              <input {...getInputProps()} />
              {acceptedFiles[0] ? (
                <div style={styles.fileAccepted}>
                  <div style={styles.fileIcon}>📄</div>
                  <div style={styles.fileName}>{acceptedFiles[0].name}</div>
                  <div style={styles.fileSize}>{(acceptedFiles[0].size / 1024 / 1024).toFixed(2)} MB — Ready to upload</div>
                </div>
              ) : (
                <div style={styles.dropzoneInner}>
                  <div style={styles.uploadIcon}>⬆</div>
                  <div style={styles.dropTitle}>{isDragActive ? 'Drop your PDF here' : 'Drag & drop your PDF print here'}</div>
                  <div style={styles.dropSub}>or click to browse files</div>
                  <div style={styles.dropHint}>Supports PDF files only</div>
                </div>
              )}
            </div>
 
            {/* PROJECT INFO FORM */}
            <div style={styles.form}>
              <h2 style={styles.formTitle}>Project Information</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Customer Name</label>
                  <input style={styles.input} placeholder="e.g. Carlson Hydraulics" value={formData.customerName} onChange={e => setFormData({...formData, customerName: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Part Number *</label>
                  <input style={styles.input} placeholder="e.g. AM-312-009" value={formData.partNumber} onChange={e => setFormData({...formData, partNumber: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Revision</label>
                  <input style={styles.input} placeholder="e.g. C" value={formData.revision} onChange={e => setFormData({...formData, revision: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Drawing Number</label>
                  <input style={styles.input} placeholder="e.g. AM-312-009" value={formData.drawingNumber} onChange={e => setFormData({...formData, drawingNumber: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Material</label>
                  <input style={styles.input} placeholder="e.g. 6061-T6 Aluminum" value={formData.material} onChange={e => setFormData({...formData, material: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Finish</label>
                  <input style={styles.input} placeholder="e.g. Anodize, Type II" value={formData.finish} onChange={e => setFormData({...formData, finish: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Units</label>
                  <select style={styles.input} value={formData.units} onChange={e => setFormData({...formData, units: e.target.value})}>
                    <option>INCH</option>
                    <option>MM</option>
                  </select>
                </div>
              </div>
            </div>
 
            {error && <div style={styles.error}>{error}</div>}
 
            <button
              style={{...styles.submitBtn, ...(uploading ? styles.submitBtnDisabled : {})}}
              onClick={handleSubmit}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload & Start Review →'}
            </button>
          </div>
 
          {/* RIGHT — recent projects */}
          <div style={styles.rightCol}>
            <div style={styles.recentCard}>
              <div style={styles.recentHeader}>
                <div style={styles.recentTitle}>Recent Projects</div>
                <div style={styles.recentCount}>{recentProjects.length}</div>
              </div>
              {recentProjects.length === 0 ? (
                <div style={styles.recentEmpty}>No projects yet — upload your first print to get started.</div>
              ) : (
                recentProjects.map(p => (
                  <div key={p.id} style={styles.recentRow} onClick={() => navigate(`/review/${p.id}`)}>
                    <div style={{ ...styles.recentAccent, background: getAccentColor(p.status) }} />
                    <div style={styles.recentInfo}>
                      <div style={styles.recentName}>
                        {p.partNumber}
                        {p.revision && <span style={styles.recentRev}> Rev {p.revision}</span>}
                      </div>
                      <div style={styles.recentMeta}>
                        {p.customerName && <span>{p.customerName} · </span>}
                        {(p.bubbles || []).length} bubbles · {formatDate(p.createdAt)}
                      </div>
                    </div>
                    <div style={styles.recentRight}>
                      <span style={{ ...styles.recentStatus, background: getStatusBg(p.status), color: getStatusColor(p.status) }}>
                        {getStatusLabel(p.status)}
                      </span>
                      <button
                        style={styles.newRunSmall}
                        onClick={(e) => handleNewRun(e, p)}
                        title="New Run"
                      >+ Run</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
 
        </div>
      </div>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  sidebarBottom: { padding: '14px 16px', borderTop: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '6px' },
  companyChip: { fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' },
  userEmail: { fontSize: '11px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  signOutBtn: { background: 'transparent', border: '1px solid #1e2d45', borderRadius: '6px', padding: '6px 12px', fontSize: '11.5px', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 32px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 },
  headerSub: { fontSize: '13px', color: '#9ca3af', margin: '3px 0 0' },
  workspace: { flex: 1, display: 'flex', overflow: 'hidden' },
  leftCol: { flex: '0 0 820px', padding: '28px 24px 28px 32px', overflowY: 'auto' },
  rightCol: { flex: 1, padding: '28px 24px 28px 0', overflowY: 'auto', minWidth: '260px' },
  searchSection: { position: 'relative', marginBottom: '0' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '10px 16px' },
  searchIcon: { fontSize: '16px', flexShrink: 0 },
  searchInput: { flex: 1, border: 'none', background: 'transparent', fontSize: '14px', color: '#111827', fontFamily: 'inherit', outline: 'none' },
  clearBtn: { background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px', padding: '0' },
  searchResults: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', marginTop: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden' },
  searchLoading: { padding: '16px 20px', fontSize: '13px', color: '#9ca3af' },
  searchEmpty: { padding: '16px 20px', fontSize: '13px', color: '#9ca3af' },
  searchResult: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f9fafb' },
  resultLeft: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, cursor: 'pointer' },
  resultIcon: { fontSize: '20px' },
  resultName: { fontSize: '13.5px', fontWeight: '600', color: '#111827' },
  resultRev: { color: '#9ca3af', fontWeight: '400' },
  resultJob: { color: '#2563EB', fontWeight: '500', fontSize: '12px' },
  resultMeta: { fontSize: '11.5px', color: '#9ca3af', marginTop: '2px' },
  resultRight: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  resultStatus: { fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '20px' },
  openBtn: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', color: '#2563EB', cursor: 'pointer', fontFamily: 'inherit' },
  newRunBtn: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', color: '#16a34a', cursor: 'pointer', fontFamily: 'inherit' },
  dividerRow: { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' },
  dividerLine: { flex: 1, height: '1px', background: '#e5e7eb' },
  dividerText: { fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' },
  dropzone: { border: '2px dashed #d1d5db', borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer', background: '#fff', marginBottom: '20px', transition: 'all 0.2s' },
  dropzoneActive: { border: '2px dashed #3b82f6', background: '#eff6ff' },
  dropzoneSuccess: { border: '2px solid #16a34a', background: '#f0fdf4' },
  dropzoneInner: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' },
  uploadIcon: { fontSize: '36px', marginBottom: '6px' },
  dropTitle: { fontSize: '15px', fontWeight: '600', color: '#111827' },
  dropSub: { fontSize: '13px', color: '#6b7280' },
  dropHint: { fontSize: '12px', color: '#9ca3af', marginTop: '4px' },
  fileAccepted: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' },
  fileIcon: { fontSize: '36px' },
  fileName: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  fileSize: { fontSize: '12px', color: '#16a34a', fontWeight: '500' },
  form: { background: '#fff', borderRadius: '12px', padding: '22px', marginBottom: '20px' },
  formTitle: { fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '13px', color: '#111827', fontFamily: 'inherit', outline: 'none' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' },
  submitBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: '9px', padding: '13px 32px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  submitBtnDisabled: { background: '#93c5fd', cursor: 'not-allowed' },
  recentCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', height: '100%' },
  recentHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' },
  recentTitle: { fontSize: '13px', fontWeight: '700', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em' },
  recentCount: { background: '#eff6ff', color: '#2563EB', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' },
  recentEmpty: { fontSize: '12px', color: '#9ca3af', padding: '20px 0', textAlign: 'center' },
  recentRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  recentAccent: { width: '3px', height: '36px', borderRadius: '2px', flexShrink: 0 },
  recentInfo: { flex: 1, minWidth: 0 },
  recentName: { fontSize: '13px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  recentRev: { color: '#9ca3af', fontWeight: '400' },
  recentMeta: { fontSize: '11px', color: '#9ca3af', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  recentRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  recentStatus: { fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' },
  newRunSmall: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '5px', padding: '3px 8px', fontSize: '10px', fontWeight: '600', color: '#16a34a', cursor: 'pointer', fontFamily: 'inherit' },
};
 
export default Upload;